import { useEffect, useRef } from "react";
import type { TrimSnapshot, ViewportState } from "../types/project";
import type { WaveformPyramid } from "../waveform/types";
import {
  dragViewport,
  overviewFractionToFrame,
  zoomViewport,
} from "../core/viewport";

type Props = {
  mode: "overview" | "detail";
  pyramid: WaveformPyramid | null;
  dataStartFrame?: number;
  waveformSource?: "detail" | "overview" | "empty";
  loading?: boolean;
  durationFrames: number;
  sampleRate: number;
  viewport: ViewportState;
  selection: TrimSnapshot;
  playheadFrame: number;
  onSeek: (frame: number) => void;
  onViewportChange?: (viewport: ViewportState) => void;
  label: string;
};

type PointerInfo = { x: number; y: number };

export function WaveformCanvas({
  mode,
  pyramid,
  dataStartFrame = 0,
  waveformSource = pyramid ? "detail" : "empty",
  loading = false,
  durationFrames,
  sampleRate,
  viewport,
  selection,
  playheadFrame,
  onSeek,
  onViewportChange,
  label,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pointers = useRef(new Map<number, PointerInfo>());
  const gesture = useRef<{
    startViewport: ViewportState;
    startDistance: number;
    startCentroid: number;
    downX: number;
    moved: boolean;
  } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const draw = (): void =>
      drawWaveform(canvas, {
        mode,
        pyramid,
        dataStartFrame,
        waveformSource,
        loading,
        durationFrames,
        viewport,
        selection,
        playheadFrame,
      });
    draw();
    const observer = new ResizeObserver(draw);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [
    mode,
    pyramid,
    dataStartFrame,
    waveformSource,
    loading,
    durationFrames,
    viewport,
    selection,
    playheadFrame,
  ]);

  const pointerDown = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    event.currentTarget.setPointerCapture(event.pointerId);
    pointers.current.set(event.pointerId, {
      x: event.nativeEvent.offsetX,
      y: event.nativeEvent.offsetY,
    });
    const values = [...pointers.current.values()];
    gesture.current = {
      startViewport: viewport,
      startDistance:
        values.length >= 2 ? Math.abs(values[1].x - values[0].x) : 0,
      startCentroid:
        values.reduce((sum, point) => sum + point.x, 0) / values.length,
      downX: event.nativeEvent.offsetX,
      moved: false,
    };
  };

  const pointerMove = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (
      !pointers.current.has(event.pointerId) ||
      !gesture.current ||
      mode !== "detail" ||
      !onViewportChange
    )
      return;
    const previous = pointers.current.get(event.pointerId)!;
    const current = {
      x: event.nativeEvent.offsetX,
      y: event.nativeEvent.offsetY,
    };
    pointers.current.set(event.pointerId, current);
    const values = [...pointers.current.values()];
    const width = event.currentTarget.clientWidth;
    if (values.length >= 2) {
      const distance = Math.max(1, Math.abs(values[1].x - values[0].x));
      const scale =
        gesture.current.startDistance > 0
          ? distance / gesture.current.startDistance
          : 1;
      onViewportChange(
        zoomViewport(
          gesture.current.startViewport,
          scale,
          gesture.current.startCentroid / width,
          durationFrames,
          sampleRate,
        ),
      );
      gesture.current.moved = true;
    } else {
      const delta = current.x - previous.x;
      if (Math.abs(current.x - gesture.current.downX) > 3)
        gesture.current.moved = true;
      onViewportChange(
        dragViewport(viewport, delta, width, durationFrames, sampleRate),
      );
    }
  };

  const pointerUp = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    const activeGesture = gesture.current;
    pointers.current.delete(event.pointerId);
    if (activeGesture && !activeGesture.moved && pointers.current.size === 0) {
      const fraction =
        event.nativeEvent.offsetX /
        Math.max(1, event.currentTarget.clientWidth);
      if (mode === "overview")
        onSeek(overviewFractionToFrame(fraction, durationFrames));
      else {
        const left = viewport.centerFrame - viewport.visibleFrames / 2;
        onSeek(left + fraction * viewport.visibleFrames);
      }
    }
    if (pointers.current.size === 0) gesture.current = null;
  };

  const wheel = (event: React.WheelEvent<HTMLCanvasElement>): void => {
    if (mode !== "detail" || !onViewportChange) return;
    event.preventDefault();
    const scale = Math.exp(-event.deltaY * 0.002);
    onViewportChange(
      zoomViewport(
        viewport,
        scale,
        event.nativeEvent.offsetX / event.currentTarget.clientWidth,
        durationFrames,
        sampleRate,
      ),
    );
  };

  return (
    <canvas
      ref={canvasRef}
      className={`waveform waveform--${mode}`}
      aria-label={label}
      role="img"
      tabIndex={0}
      onPointerDown={pointerDown}
      onPointerMove={pointerMove}
      onPointerUp={pointerUp}
      onPointerCancel={pointerUp}
      onWheel={wheel}
      data-waveform-source={waveformSource}
      data-waveform-loading={loading ? "true" : "false"}
    />
  );
}

function drawWaveform(
  canvas: HTMLCanvasElement,
  props: Pick<
    Props,
    | "mode"
    | "pyramid"
    | "dataStartFrame"
    | "waveformSource"
    | "loading"
    | "durationFrames"
    | "viewport"
    | "selection"
    | "playheadFrame"
  >,
): void {
  const width = Math.max(1, canvas.clientWidth);
  const height = Math.max(1, canvas.clientHeight);
  const dpr = Math.min(3, window.devicePixelRatio || 1);
  if (
    canvas.width !== Math.round(width * dpr) ||
    canvas.height !== Math.round(height * dpr)
  ) {
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
  }
  const context = canvas.getContext("2d");
  if (!context) return;
  context.setTransform(dpr, 0, 0, dpr, 0, 0);
  context.clearRect(0, 0, width, height);
  context.fillStyle = "#0b2743";
  context.fillRect(0, 0, width, height);

  const rangeStart =
    props.mode === "overview"
      ? 0
      : props.viewport.centerFrame - props.viewport.visibleFrames / 2;
  const rangeEnd =
    props.mode === "overview"
      ? props.durationFrames
      : props.viewport.centerFrame + props.viewport.visibleFrames / 2;
  const rangeFrames = Math.max(1, rangeEnd - rangeStart);
  const frameToX = (frame: number): number =>
    ((frame - rangeStart) / rangeFrames) * width;

  context.fillStyle = "rgba(0, 0, 0, 0.46)";
  context.fillRect(
    0,
    0,
    Math.max(0, frameToX(props.selection.startFrame)),
    height,
  );
  const endX = frameToX(props.selection.endFrame);
  context.fillRect(Math.min(width, endX), 0, Math.max(0, width - endX), height);

  if (props.pyramid?.levels[0]) {
    const level = props.pyramid.levels[0];
    context.strokeStyle = "#69d9ef";
    context.lineWidth = Math.max(1, 1 / dpr);
    context.beginPath();
    const centerY = height / 2;
    const amplitude = height * 0.43;
    for (let pixel = 0; pixel < width; pixel += 1) {
      const frame = rangeStart + (pixel / width) * rangeFrames;
      const index = Math.floor(
        (frame - (props.dataStartFrame ?? 0)) / level.framesPerBucket,
      );
      if (index < 0 || index >= level.mins.length) continue;
      context.moveTo(pixel + 0.5, centerY + level.mins[index] * amplitude);
      context.lineTo(pixel + 0.5, centerY + level.maxs[index] * amplitude);
    }
    context.stroke();
  } else {
    context.fillStyle = "rgba(255,255,255,.55)";
    context.font = "14px -apple-system, sans-serif";
    context.textAlign = "center";
    context.fillText("波形を解析中…", width / 2, height / 2);
  }

  if (props.pyramid && props.loading && props.waveformSource === "overview") {
    context.fillStyle = "rgba(255,255,255,.68)";
    context.font = "11px -apple-system, sans-serif";
    context.textAlign = "right";
    context.textBaseline = "alphabetic";
    context.fillText("詳細を読込中…", width - 9, height - 9);
  }

  drawMarker(
    context,
    frameToX(props.selection.startFrame),
    height,
    "#39d98a",
    "S",
    props.mode === "overview",
  );
  drawMarker(
    context,
    frameToX(props.selection.endFrame),
    height,
    "#ffad42",
    "E",
    props.mode === "overview",
  );
  const cursorX =
    props.mode === "overview" ? frameToX(props.playheadFrame) : width / 2;
  context.strokeStyle = "#ff4d63";
  context.lineWidth = 2;
  context.beginPath();
  context.moveTo(cursorX, 0);
  context.lineTo(cursorX, height);
  context.stroke();
}

function drawMarker(
  context: CanvasRenderingContext2D,
  x: number,
  height: number,
  color: string,
  label: string,
  showLabel: boolean,
): void {
  if (x < -6 || x > context.canvas.clientWidth + 6) return;
  context.strokeStyle = color;
  context.lineWidth = 2;
  context.beginPath();
  context.moveTo(x, 0);
  context.lineTo(x, height);
  context.stroke();
  if (!showLabel) return;
  context.fillStyle = color;
  context.fillRect(x - 9, 0, 18, 18);
  context.fillStyle = "#04101e";
  context.font = "700 11px -apple-system, sans-serif";
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(label, x, 9);
}
